import { useEffect } from 'react';

import { envConfig } from '#constants/envConfig';
import { requestGuardedNavigation } from '../model/guardedNavigation';
import { nativeBackOverlay } from '../model/nativeBackForms';
import { runNativeBackHandler } from '../model/nativeBackStack';

/**
 * Wires Android's system back button in the native shell. No-op / tree-shaken on web.
 *
 * Without a listener the button does *nothing at all* on the first screen: the
 * @capacitor/app plugin installs an always-enabled OnBackPressedCallback, so it goes
 * back through history while there is history and then swallows the event — the
 * activity is never finished and the app cannot be closed with the button. That reads
 * as a frozen app, so Android users hit it early and loudly.
 *
 * Order of handling, matching what people expect from a native app:
 *  1. an open dialog/sheet closes;
 *  2. экран с постоянным родителем уводит к родителю (переписка → список чатов) — см.
 *     `nativeBackStack`;
 *  3. otherwise navigate back through history;
 *  4. on the first screen minimise (like Home), NOT exit — exiting would make the
 *     next launch a cold start, and Capacitor cannot restore the previous state.
 *
 * Overlays are closed by dispatching Escape rather than through a registry: Ark UI
 * (Chakra 3) dismisses the topmost open layer on a document-level Escape, and the
 * project has no global store of open dialogs to consult.
 *
 * Mount once, high in the tree.
 */
export const useNativeBackButton = () => {
  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    let remove: (() => void) | undefined;
    let active = true;

    (async () => {
      const { App } = await import('@capacitor/app');

      const handle = await App.addListener('backButton', ({ canGoBack }) => {
        const overlay = nativeBackOverlay();
        const scope = overlay?.formBoundary ? overlay.node : document.body;
        const previousStep = !overlay && !!document.querySelector('[data-native-back-step="true"]');
        requestGuardedNavigation(
          () => {
            if (closeTopmostOverlay()) return;
            if (runNativeBackHandler()) return;
            if (canGoBack) window.history.back();
            else void App.minimizeApp();
          },
          scope,
          overlay ? overlay.formBoundary : !previousStep
        );
      });

      if (active) {
        remove = () => handle.remove();
      } else {
        handle.remove();
      }
    })();

    return () => {
      active = false;
      remove?.();
    };
  }, []);
};

/**
 * Слои, которые системная «Назад» закрывает, прежде чем уйти со страницы.
 *
 * Окна и шторки узнаются по роли, остальное - по паре «область + часть» от Ark UI: у меню,
 * выпадающего списка и подсказки выбора роль не `dialog`, и по одной только роли они проходили
 * мимо. Тем же нажатием сворачивалось всё приложение - при открытом меню аватара, на форме
 * заявки при открытом выборе игры.
 *
 * Перечисляем поимённо, а не берём всё с `data-state="open"`: этот признак есть и у того, что
 * «Назад» закрывать не должна и не умеет - у раскрытого свёрнутого блока, например. Такой слой
 * съел бы нажатие впустую, и кнопка выглядела бы сломанной.
 */
const DISMISSABLE_LAYERS = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[data-scope="menu"][data-part="content"][data-state="open"]',
  '[data-scope="select"][data-part="content"][data-state="open"]',
  '[data-scope="combobox"][data-part="content"][data-state="open"]',
  '[data-scope="popover"][data-part="content"][data-state="open"]',
  '[data-scope="dialog"][data-part="content"][data-state="open"]',
  '[data-scope="drawer"][data-part="content"][data-state="open"]',
].join(', ');

/**
 * Returns true when something dismissable was on screen, so the back press is spent
 * on closing it instead of navigating away and leaving the overlay orphaned.
 */
export const closeTopmostOverlay = (): boolean => {
  if (!document.querySelector(DISMISSABLE_LAYERS)) {
    return false;
  }

  // A picker can consume Back and return to its parent step without closing the dialog.
  document.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true })
  );
  return true;
};
